import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Check, X, Edit3, Flag, Beaker } from "lucide-react";
import { api, extractErrorMessage } from "@/lib/api";

/**
 * Admin-only CRUD panel for creditor flag rules.
 *
 * Each rule defines a regex that, when it matches the müvekkil (alacaklı) adı,
 * forces a boolean flag on the prim row — currently either `varlik_sirketi` or
 * `sehir_disi`. The PrimModal consumes these rules live and locks the matching
 * checkbox so edits stay consistent.
 */
const FLAG_KEY_LABELS = {
  varlik_sirketi: "Varlık Şirketi",
  sehir_disi: "Şehir Dışı",
  tag: "Etiket (Chip)",
};

export default function CreditorFlagRulesPanel() {
  const [rules, setRules] = useState([]);
  const [allowedKeys, setAllowedKeys] = useState(["varlik_sirketi", "sehir_disi"]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState({
    name: "",
    pattern: "",
    flag_key: "varlik_sirketi",
    flag_value: true,
    tag: "",
    is_active: true,
    aciklama: "",
    priority: 100,
  });
  const [editing, setEditing] = useState({}); // {id: {...}}
  const [testSample, setTestSample] = useState("");
  const [testResult, setTestResult] = useState(null); // {match: bool}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/creditor-flag-rules");
      setRules(r.data?.rows || []);
      if (Array.isArray(r.data?.allowed_flag_keys)) setAllowedKeys(r.data.allowed_flag_keys);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Yüklenemedi"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.name.trim()) return toast.error("Kural adı zorunlu");
    if (!draft.pattern.trim()) return toast.error("Pattern zorunlu");
    if (draft.flag_key === "tag" && !(draft.tag || "").trim()) return toast.error("Etiket adı zorunlu");
    try {
      await api.post("/creditor-flag-rules", {
        name: draft.name.trim(),
        pattern: draft.pattern.trim(),
        flag_key: draft.flag_key,
        flag_value: !!draft.flag_value,
        tag: draft.flag_key === "tag" ? draft.tag.trim() : null,
        is_active: !!draft.is_active,
        aciklama: draft.aciklama || "",
        priority: Number(draft.priority) || 100,
      });
      toast.success("Kural eklendi");
      setAddOpen(false);
      setDraft({
        name: "", pattern: "", flag_key: "varlik_sirketi", flag_value: true,
        tag: "", is_active: true, aciklama: "", priority: 100,
      });
      setTestSample("");
      setTestResult(null);
      load();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Eklenemedi"));
    }
  };

  const startEdit = (rule) => {
    setEditing({ ...editing, [rule.id]: {
      name: rule.name,
      pattern: rule.pattern,
      flag_key: rule.flag_key,
      flag_value: !!rule.flag_value,
      tag: rule.tag || "",
      is_active: !!rule.is_active,
      aciklama: rule.aciklama || "",
      priority: rule.priority ?? 100,
    }});
  };
  const cancelEdit = (id) => {
    const next = { ...editing };
    delete next[id];
    setEditing(next);
  };
  const saveEdit = async (id) => {
    const e = editing[id];
    if (!e) return;
    try {
      await api.put(`/creditor-flag-rules/${id}`, {
        ...e,
        priority: Number(e.priority) || 100,
      });
      toast.success("Güncellendi");
      cancelEdit(id);
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Güncellenemedi"));
    }
  };

  const toggleActive = async (rule) => {
    try {
      await api.put(`/creditor-flag-rules/${rule.id}`, { is_active: !rule.is_active });
      toast.success(rule.is_active ? "Pasifleştirildi" : "Aktifleştirildi");
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Değiştirilemedi"));
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`"${rule.name}" kuralını silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/creditor-flag-rules/${rule.id}`);
      toast.success("Silindi");
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Silinemedi"));
    }
  };

  const runTest = async () => {
    if (!draft.pattern.trim()) return toast.error("Önce pattern yazın");
    try {
      const r = await api.post("/creditor-flag-rules/test", {
        pattern: draft.pattern.trim(),
        sample: testSample,
      });
      setTestResult(r.data);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Test edilemedi"));
    }
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="creditor-flag-rules-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="font-semibold text-sm text-slate-900 inline-flex items-center gap-1.5">
            <Flag size={14} className="text-indigo-600" />
            Müvekkil Oto-Bayrak Kuralları
          </h4>
          <p className="text-xs text-slate-600 mt-0.5">
            Müvekkil adı belirli bir <code className="bg-slate-200 px-1 rounded">regex</code> ile eşleştiğinde Prim satırının <strong>Varlık Şirketi</strong> veya <strong>Şehir Dışı</strong> bayrağı otomatik işaretlenir. Örn: <code className="bg-slate-200 px-1 rounded">varl[ıi]k\s+y[öo]netim</code> → Varlık Şirketi.
          </p>
        </div>
        <button
          data-testid="flag-rule-add-btn"
          onClick={() => setAddOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-md text-xs font-semibold"
        >
          <Plus size={14} /> Kural Ekle
        </button>
      </div>

      {addOpen && (
        <div className="mb-3 p-3 bg-white border border-slate-300 rounded-lg space-y-2" data-testid="flag-rule-add-form">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
            <input
              data-testid="flag-rule-name"
              placeholder="Kural adı (örn: Factoring Şirketleri)"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className="col-span-2 px-2 py-1.5 border border-slate-200 rounded text-sm"
            />
            <input
              data-testid="flag-rule-pattern"
              placeholder="Regex pattern (örn: factoring)"
              value={draft.pattern}
              onChange={(e) => { setDraft({ ...draft, pattern: e.target.value }); setTestResult(null); }}
              className="col-span-2 px-2 py-1.5 border border-slate-200 rounded text-sm font-mono"
            />
            <select
              data-testid="flag-rule-flagkey"
              value={draft.flag_key}
              onChange={(e) => setDraft({ ...draft, flag_key: e.target.value })}
              className="px-2 py-1.5 border border-slate-200 rounded text-sm"
            >
              {allowedKeys.map((k) => <option key={k} value={k}>{FLAG_KEY_LABELS[k] || k}</option>)}
            </select>
            {draft.flag_key === "tag" ? (
              <input
                data-testid="flag-rule-tag"
                placeholder="Etiket (örn: Factoring)"
                value={draft.tag}
                onChange={(e) => setDraft({ ...draft, tag: e.target.value })}
                className="px-2 py-1.5 border border-slate-200 rounded text-sm font-semibold"
              />
            ) : (
              <select
                data-testid="flag-rule-flagvalue"
                value={String(draft.flag_value)}
                onChange={(e) => setDraft({ ...draft, flag_value: e.target.value === "true" })}
                className="px-2 py-1.5 border border-slate-200 rounded text-sm"
              >
                <option value="true">Evet (True)</option>
                <option value="false">Hayır (False)</option>
              </select>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
            <input
              data-testid="flag-rule-aciklama"
              placeholder="Açıklama (opsiyonel)"
              value={draft.aciklama}
              onChange={(e) => setDraft({ ...draft, aciklama: e.target.value })}
              className="md:col-span-3 px-2 py-1.5 border border-slate-200 rounded text-sm"
            />
            <input
              type="number"
              placeholder="Öncelik (küçük = önce)"
              value={draft.priority}
              onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
              className="px-2 py-1.5 border border-slate-200 rounded text-sm font-mono-tab"
            />
            <label className="inline-flex items-center gap-1.5 text-xs font-semibold">
              <input
                type="checkbox"
                checked={draft.is_active}
                onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
              />
              Aktif
            </label>
            <button
              data-testid="flag-rule-save"
              onClick={create}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-xs font-semibold"
            >
              Kaydet
            </button>
          </div>
          <div className="pt-2 border-t border-slate-100 flex items-center gap-2">
            <Beaker size={14} className="text-slate-500" />
            <input
              data-testid="flag-rule-test-sample"
              placeholder='Test metni (örn: "Hedef Varlık Yönetim A.Ş.")'
              value={testSample}
              onChange={(e) => setTestSample(e.target.value)}
              className="flex-1 px-2 py-1 border border-slate-200 rounded text-sm"
            />
            <button
              onClick={runTest}
              data-testid="flag-rule-test-run"
              className="px-3 py-1 bg-slate-700 hover:bg-slate-800 text-white rounded text-xs font-semibold"
            >
              Test Et
            </button>
            {testResult && (
              <span
                data-testid="flag-rule-test-result"
                className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded ${
                  testResult.match ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                }`}
              >
                {testResult.match ? "✓ Eşleşti" : "✗ Eşleşmedi"}
              </span>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-6 text-slate-400 text-sm">Yükleniyor...</div>
      ) : rules.length === 0 ? (
        <div className="text-center py-6 text-slate-400 text-sm">Henüz kural tanımlı değil</div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-md border border-slate-200">
          <table className="w-full text-xs" data-testid="flag-rules-table">
            <thead className="bg-slate-100 uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Ad</th>
                <th className="text-left px-3 py-2">Pattern</th>
                <th className="text-left px-3 py-2">Bayrak</th>
                <th className="text-left px-3 py-2">Değer</th>
                <th className="text-right px-3 py-2">Öncelik</th>
                <th className="text-center px-3 py-2">Durum</th>
                <th className="text-right px-3 py-2">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => {
                const e = editing[rule.id];
                if (e) {
                  return (
                    <tr key={rule.id} className="border-t border-slate-200 bg-amber-50" data-testid={`flag-rule-edit-${rule.id}`}>
                      <td className="px-3 py-2">
                        <input value={e.name} onChange={(ev) => setEditing({ ...editing, [rule.id]: { ...e, name: ev.target.value }})} className="w-full px-2 py-1 border border-slate-300 rounded" />
                      </td>
                      <td className="px-3 py-2">
                        <input value={e.pattern} onChange={(ev) => setEditing({ ...editing, [rule.id]: { ...e, pattern: ev.target.value }})} className="w-full px-2 py-1 border border-slate-300 rounded font-mono" />
                      </td>
                      <td className="px-3 py-2">
                        <select value={e.flag_key} onChange={(ev) => setEditing({ ...editing, [rule.id]: { ...e, flag_key: ev.target.value }})} className="px-2 py-1 border border-slate-300 rounded">
                          {allowedKeys.map((k) => <option key={k} value={k}>{FLAG_KEY_LABELS[k] || k}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        {e.flag_key === "tag" ? (
                          <input
                            value={e.tag || ""}
                            onChange={(ev) => setEditing({ ...editing, [rule.id]: { ...e, tag: ev.target.value }})}
                            placeholder="Etiket"
                            className="w-full px-2 py-1 border border-slate-300 rounded font-semibold"
                          />
                        ) : (
                          <select value={String(e.flag_value)} onChange={(ev) => setEditing({ ...editing, [rule.id]: { ...e, flag_value: ev.target.value === "true" }})} className="px-2 py-1 border border-slate-300 rounded">
                            <option value="true">Evet</option>
                            <option value="false">Hayır</option>
                          </select>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" value={e.priority} onChange={(ev) => setEditing({ ...editing, [rule.id]: { ...e, priority: ev.target.value }})} className="w-20 px-2 py-1 border border-slate-300 rounded text-right font-mono-tab" />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked={!!e.is_active} onChange={(ev) => setEditing({ ...editing, [rule.id]: { ...e, is_active: ev.target.checked }})} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1">
                          <button onClick={() => saveEdit(rule.id)} className="w-7 h-7 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center justify-center"><Check size={14} /></button>
                          <button onClick={() => cancelEdit(rule.id)} className="w-7 h-7 rounded border border-slate-200 text-slate-500 inline-flex items-center justify-center"><X size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={rule.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`flag-rule-row-${rule.id}`}>
                    <td className="px-3 py-2 font-semibold text-slate-900">
                      {rule.name}
                      {rule.aciklama && <div className="text-[10px] text-slate-500 font-normal">{rule.aciklama}</div>}
                    </td>
                    <td className="px-3 py-2 font-mono text-slate-700">{rule.pattern}</td>
                    <td className="px-3 py-2 text-slate-700">{FLAG_KEY_LABELS[rule.flag_key] || rule.flag_key}</td>
                    <td className="px-3 py-2">
                      {rule.flag_key === "tag" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-100 text-sky-700 border border-sky-200">
                          # {rule.tag || "—"}
                        </span>
                      ) : (
                        <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold ${rule.flag_value ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                          {rule.flag_value ? "Evet" : "Hayır"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-tab text-slate-600">{rule.priority ?? 100}</td>
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => toggleActive(rule)}
                        className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${rule.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
                      >
                        {rule.is_active ? "Aktif" : "Pasif"}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => startEdit(rule)} className="w-7 h-7 rounded text-slate-600 hover:bg-slate-100 inline-flex items-center justify-center"><Edit3 size={14} /></button>
                        <button onClick={() => remove(rule)} className="w-7 h-7 rounded text-rose-600 hover:bg-rose-50 inline-flex items-center justify-center"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
